import React from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";

import { formatDateWithTime } from "../../../../utils/date";
import CustomText from "../../../common/general/CustomText/CustomText";
import { getStatusColor } from "../SubmissionRow";
import {
  AssessmentSubmission,
  useGetAssessmentSubmissionAttemptHistoryQuery,
  useGetAssessmentSubmissionsDetailsQuery,
} from "../../../../graphql/generated/graphql";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";

const AttemptHistory: React.FC = () => {
  const { selectedSubmissionData } = useGradingCenterSubmissions();

  const {
    data: submissionDetailsData,
    loading: submissionDetailsLoading,
    error: submissionDetailsError,
  } = useGetAssessmentSubmissionsDetailsQuery({
    variables: {
      assessmentSubmissionId: selectedSubmissionData?.id || "",
      studentId: selectedSubmissionData?.student?.id || "",
    },
    skip: !selectedSubmissionData?.id || !selectedSubmissionData?.student?.id,
  });

  const assessmentId = submissionDetailsData?.getAssessmentSubmissionDetails?.data?.assessmentId;

  const {
    data: attemptHistoryData,
    loading: attemptHistoryLoading,
    error: attemptHistoryError,
  } = useGetAssessmentSubmissionAttemptHistoryQuery({
    variables: {
      assessmentId: assessmentId || "",
      userId: selectedSubmissionData?.student?.id || "",
    },
    skip: !assessmentId || !selectedSubmissionData?.student?.id,
  });

  const attemptHistory = attemptHistoryData?.getAttemptHistory?.data?.assessmentSubmissions;

  const calculateTimeSpent = (startedAt: string, completedAt: string) => {
    if (!startedAt || !completedAt) return "0 min";

    const startedAtDate = new Date(startedAt);
    const completedAtDate = new Date(completedAt);
    const timeSpent = completedAtDate.getTime() - startedAtDate.getTime();
    const minutes = Math.floor(timeSpent / (1000 * 60));

    if (Number.isNaN(minutes)) {
      return "0 min";
    }

    return `${minutes} min`;
  };

  const getAttemptNumber = (submission: AssessmentSubmission) => {
    // Sort by creation date to get proper attempt numbers
    const sortedSubmissions = [...(attemptHistory || [])].sort(
      (a, b) => new Date(a.created || "").getTime() - new Date(b.created || "").getTime()
    );
    const attemptIndex = sortedSubmissions.findIndex((s) => s.id === submission.id);
    return attemptIndex + 1;
  };

  const isCurrentAttempt = (submission: AssessmentSubmission) => {
    return submission.id === selectedSubmissionData?.id;
  };

  if (submissionDetailsLoading || attemptHistoryLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#3E68FE" />
        <CustomText text="Loading attempt history..." size="s" style={styles.loadingText} />
      </View>
    );
  }

  if (submissionDetailsError || attemptHistoryError) {
    return (
      <View style={styles.errorContainer}>
        <CustomText text="Error loading attempt history" size="s" style={styles.errorText} />
      </View>
    );
  }

  if (!attemptHistory || attemptHistory.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <CustomText text="No previous attempts found" size="s" style={styles.emptyText} />
      </View>
    );
  }

  // Sort submissions by creation date (oldest first)
  const sortedSubmissions = [...(attemptHistory || [])].sort(
    (a, b) => new Date(a.created || "").getTime() - new Date(b.created || "").getTime()
  );

  return (
    <View style={styles.attemptHistorySection}>
      <CustomText text="Previous Attempts" size="m" weight="bold" style={styles.sectionTitle} />

      <View style={styles.attemptHistoryList}>
        {sortedSubmissions.reverse().map((submission, index) => {
          const attemptNumber = getAttemptNumber(submission as AssessmentSubmission);
          const isCurrent = isCurrentAttempt(submission as AssessmentSubmission);

          const statusColor = getStatusColor(submission.hasTeacherGraded ? "Completed" : "Needs Review");

          return (
            <View
              key={submission.id}
              style={[
                styles.attemptRow,
                isCurrent && styles.currentAttempt,
                index === sortedSubmissions.length - 1 && styles.lastRow,
              ]}>
              <View style={styles.attemptInfo}>
                <View style={styles.attemptIdentifier}>
                  {isCurrent && (
                    <View style={styles.currentBadge}>
                      <CustomText text="Current" size="xs" weight="bold" style={styles.currentBadgeText} />
                    </View>
                  )}
                  <CustomText
                    text="attempt_number"
                    interpolation={{ attemptNumber }}
                    size="s"
                    weight="bold"
                    style={styles.attemptIdentifierText}
                  />
                </View>
                <CustomText
                  text={formatDateWithTime(submission.startedAt || submission.created || "")}
                  size="xs"
                  weight="normal"
                  style={styles.attemptDate}
                  useTranslationText={false}
                />
              </View>
              <CustomText
                text={`${submission.totalPoints || 0}/${submission.maximumPoints || 100}`}
                weight="ultraBold"
                style={styles.attemptScore}
                useTranslationText={false}
              />
              <CustomText
                text={calculateTimeSpent(submission.startedAt || "", submission.completedAt || "")}
                size="s"
                weight="normal"
                style={styles.attemptTime}
                useTranslationText={false}
              />
              <View style={styles.attemptStatus}>
                <CustomText
                  text={submission.hasTeacherGraded ? "Completed" : "Needs Review"}
                  size="xs"
                  weight="bold"
                  style={[styles.statusBadgeMini, { color: statusColor.text, backgroundColor: statusColor.bg }]}
                />
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  attemptHistorySection: {
    marginBottom: 24,
  },
  sectionTitle: {
    color: "#374151",
    marginBottom: 16,
  },
  attemptHistoryList: {
    backgroundColor: "#EAECF0",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#EAECF0",
    overflow: "hidden",
  },
  attemptRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  lastRow: {
    borderBottomWidth: 0,
  },
  currentAttempt: {
    backgroundColor: "#EFF6FF",
    borderBottomColor: "#BFDBFE",
  },
  attemptInfo: {
    flex: 1,
    flexDirection: "column",
    gap: 4,
  },
  attemptIdentifier: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  attemptIdentifierText: {
    color: "#111827",
    fontSize: 14,
    fontWeight: "600",
  },
  currentBadge: {
    backgroundColor: "#3E68FE",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  currentBadgeText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  attemptDate: {
    color: "#4B5563",
    fontSize: 13,
  },
  attemptScore: {
    color: "#3E68FE",
    minWidth: 80,
    textAlign: "center",
  },
  attemptTime: {
    color: "#475467",
    minWidth: 60,
    textAlign: "center",
  },
  attemptStatus: {
    minWidth: 100,
    alignItems: "center",
  },
  statusBadgeMini: {
    backgroundColor: "#D1FAE5",
    color: "#047857",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    fontSize: 11,
    fontWeight: "600",
    letterSpacing: 0.5,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  loadingText: {
    color: "#6B7280",
    marginTop: 12,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  errorText: {
    color: "#DC2626",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  emptyText: {
    color: "#6B7280",
  },
});

export default AttemptHistory;
